/**
 * PasswordInput
 *
 * Drop-in replacement for `<Input type="password" />` that adds a small
 * eye icon on the right which toggles the field between masked (dots)
 * and plain-text so the user can visually verify what they've typed.
 *
 * Props are the same as the shadcn `<Input>` component; forwarded ref is
 * kept so react-hook-form and native focus() calls still work.
 *
 * A data-testid on the toggle button uses the parent input's testid +
 * "-toggle" so tests can drive it deterministically.
 */
import React, { forwardRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff } from "lucide-react";

const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": testid, disabled, ...rest },
  ref
) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        {...rest}
        ref={ref}
        type={visible ? "text" : "password"}
        disabled={disabled}
        // Right padding so the value never runs under the eye icon.
        className={`pr-10 ${className}`}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        tabIndex={-1}
        aria-label={visible ? "Şifreyi gizle" : "Şifreyi göster"}
        title={visible ? "Şifreyi gizle" : "Şifreyi göster"}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-brand-navy transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
});

export default PasswordInput;
